// Register offline support: a local copy of the store's items for scanning, and a queue of
// completed sales that replays when the connection returns. Each sale carries a client_txn_id,
// so a replay that already reached the server is not recorded twice.
import { api, ApiError, NetworkError } from './api';

export interface CachedItem {
  id: string; product_id: string; brand: string; name: string; size_ml: number; category: string;
  retail_cents: number; last_unit_cost_cents: number | null; on_hand: number; codes: string[] | null;
}

const ITEMS = 'njpos.items';
const QUEUE = 'njpos.saleQueue';
const PROBLEMS = 'njpos.saleProblems';

export function cachedItems(): CachedItem[] {
  try { return JSON.parse(localStorage.getItem(ITEMS) ?? '[]'); } catch { return []; }
}

export async function refreshItemCache(): Promise<number> {
  const items = await api<CachedItem[]>('/api/items?limit=1000');
  localStorage.setItem(ITEMS, JSON.stringify(items));
  return items.length;
}

export function findCachedByCode(code: string): CachedItem | null {
  const digits = code.replace(/\D/g, '');
  if (!digits) return null;
  const gtin = digits.padStart(14, '0');
  return cachedItems().find((i) => (i.codes ?? []).includes(gtin)) ?? null;
}

export function queued(): any[] { try { return JSON.parse(localStorage.getItem(QUEUE) ?? '[]'); } catch { return []; } }
export function problems(): { sale: any; error: string }[] { try { return JSON.parse(localStorage.getItem(PROBLEMS) ?? '[]'); } catch { return []; } }

export function enqueue(sale: any) {
  localStorage.setItem(QUEUE, JSON.stringify([...queued(), sale]));
  window.dispatchEvent(new Event('njpos:queue'));
}

let flushing = false;
export async function flushQueue(): Promise<{ sent: number; left: number }> {
  if (flushing) return { sent: 0, left: queued().length };
  flushing = true;
  let sent = 0;
  try {
    for (const sale of queued()) {
      try {
        await api('/api/sales', { body: sale });
        sent++;
      } catch (e) {
        if (e instanceof NetworkError) break; // still offline; keep the rest
        // The server refused it (e.g. a price change made it below cost): park it for a manager.
        const msg = e instanceof ApiError ? e.message : String(e);
        localStorage.setItem(PROBLEMS, JSON.stringify([...problems(), { sale, error: msg }]));
      }
      localStorage.setItem(QUEUE, JSON.stringify(queued().filter((q) => q.clientTxnId !== sale.clientTxnId)));
    }
  } finally {
    flushing = false;
    window.dispatchEvent(new Event('njpos:queue'));
  }
  return { sent, left: queued().length };
}

export function startQueueLoop() {
  const tick = () => { if (queued().length) flushQueue().catch(() => {}); };
  window.addEventListener('online', tick);
  return window.setInterval(tick, 15000);
}
